import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { submitInquiryFailure, submitInquiryRequest, submitInquirySuccess } from '../../../../../state';
import { AnalyticsService } from '../../../../../services/analytics/analytics.service';

@Component({
  selector: 'wil-qualified-enquiry-form',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './qualified-enquiry-form.component.html',
  styleUrl: './qualified-enquiry-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QualifiedEnquiryFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly analytics = inject(AnalyticsService);

  @ViewChild('errorSummary') private errorSummary?: ElementRef<HTMLElement>;

  public readonly pending = signal(false);
  public readonly success = signal(false);
  public readonly submissionFailed = signal(false);
  public readonly attempted = signal(false);

  public readonly form = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    website: [''],
    projectDetails: ['', [Validators.required, Validators.minLength(20), Validators.maxLength(1200)]],
    nickname: [''],
  });

  public ngOnInit(): void {
    this.actions$.pipe(ofType(submitInquirySuccess), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.pending()) return;
      this.pending.set(false);
      this.success.set(true);
      this.form.disable();
      this.analytics.track('embedded_assessment_inquiry_success', {
        source: 'embedded_assessment_landing',
        result: 'success',
      });
    });

    this.actions$.pipe(ofType(submitInquiryFailure), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.pending()) return;
      this.pending.set(false);
      this.submissionFailed.set(true);
      this.analytics.track('embedded_assessment_inquiry_failure', {
        source: 'embedded_assessment_landing',
        result: 'error',
      });
    });
  }

  public submit(): void {
    if (this.pending()) return;

    this.attempted.set(true);
    this.submissionFailed.set(false);
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      setTimeout(() => this.errorSummary?.nativeElement.focus());
      return;
    }

    const value = this.form.getRawValue();
    if (value.nickname) return;

    this.pending.set(true);
    this.store.dispatch(
      submitInquiryRequest({
        request: {
          email: value.email,
          message: this.buildMessage(value),
          nickname: value.nickname,
          sentAt: new Date().toISOString(),
        },
      })
    );
  }

  public hasError(controlName: 'name' | 'email' | 'website' | 'projectDetails'): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (control.touched || this.attempted());
  }

  private buildMessage(value: ReturnType<typeof this.form.getRawValue>): string {
    return [`Name: ${value.name}`, `Website: ${value.website}`, '', 'Project details:', value.projectDetails].join('\n');
  }
}
